// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Modal } from "react-bootstrap";
import { Form, Col, Row } from "react-bootstrap";
import InputNumber from "rc-input-number";

import { ActionType, IAction, raiseAction } from "actions";
import { IActionChangeTariff, IActionTypeConfirmPopup } from "actions";
import { DataState } from "types";
import { IState } from "states/states";
import { ModalPanel } from "states/guistates";
import { ITariff } from "states/ocppstates";
import { getLocalDate, getLocalDateTimeIso, getLocalTime, isSet } from "utils";
import { parseDateTimeEvent } from "guiutils";

import SaveFeedback from "view/comp/SaveFeedback";

type IModalTariffItemProps = {
  isOpen: boolean;
  dataState: DataState;
  id: number | undefined;
  tariff: ITariff | undefined;
  isRestricted: boolean;
};

const mapStateToProps = (state: IState) => {
  const tariff = state.ocpp.siteTariff.find((curItem) => curItem.id === state.gui.modalTariff);
  const props: IModalTariffItemProps = {
    isOpen: state.gui.modal.panel === ModalPanel.TARIFF,
    id: state.gui.modalTariff,
    dataState: state.gui.modal.dataState,
    tariff,
    isRestricted: isSet(tariff) && tariff.siteId !== state.cached.selectedSite,
  };
  return props;
};

interface IModalTariffState {
  wasOpen: boolean;
  isChanged: boolean;
  name: string;
  currency: string;
  isActive?: boolean;
  priority?: number;
  costPerEnergy?: number;
  costPerPeriod?: number;
  validFrom?: number;
  validUntil?: number;
  timeStart?: number;
  timeEnd?: number;
}

interface IModalTariffItemDispatch {
  onConfirmDelete: (id: number, title: string, message: string) => void;
  onSaveClick: (id: number | undefined, state: IModalTariffState) => void;
  onCancelClick: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalTariffItemDispatch = {
    onConfirmDelete: (id: number, title: string, message: string) => {
      const confirmPayload: IActionChangeTariff = {
        id,
        name: undefined,
      };
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_SAVE_OCPP_TARIFF_REQ,
        actionPayload: confirmPayload,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
      // the delete operation takes some time so the screen update will not be instantenious
      // as the confirm popup is closed immediately
    },
    onSaveClick: (id: number | undefined, state: IModalTariffState) => {
      // remove unused/renamed properties from state
      const { wasOpen, isChanged, ...data } = state;
      const payload: IActionChangeTariff = {
        id,
        ...data,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_OCPP_TARIFF_REQ, payload));
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  confirmDeleteTitle: {
    id: "Delete cost tariff",
  },
  confirmDeleteBody: {
    id: "This will delete this tariff. All associated costs will be lost. Are you sure?",
  },
});

class ModalTariffItemComponent extends React.Component<
  IModalTariffItemProps & IModalTariffItemDispatch & WrappedComponentProps,
  IModalTariffState
> {
  public state: IModalTariffState = {
    wasOpen: this.props.isOpen,
    isChanged: false,
    name: "",
    currency: "EUR",
  };

  componentDidUpdate() {
    // check for changes in old value
    if (this.props.isOpen !== this.state.wasOpen) {
      this.setState({
        wasOpen: this.props.isOpen,
        isChanged: false,
        name: this.props.tariff?.name ?? "",
        currency: this.props.tariff?.currency ?? "EUR",
        // default isActive to true for new tariffs
        isActive: this.props.tariff ? this.props.tariff.isActive : true,
        priority: this.props.tariff?.priority,
        costPerEnergy: this.props.tariff?.costPerEnergy,
        costPerPeriod: this.props.tariff?.costPerPeriod,
        validFrom: this.props.tariff?.validFrom,
        validUntil: this.props.tariff?.validUntil,
        timeStart: this.props.tariff?.timeStart,
        timeEnd: this.props.tariff?.timeEnd,
      });
    }
  }

  public render() {
    if (!this.props.isOpen) return <></>; // avoid rendering of inexisting data

    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick}>
        <Modal.Header closeButton>
          <FormattedMessage id="Tariff Settings" />
        </Modal.Header>
        <Modal.Body>{this.renderSettings()}</Modal.Body>
        <Modal.Footer>
          <div className="button-bar-left">
            {isSet(this.props.id) && (
              <Button
                variant="danger"
                disabled={this.props.dataState === DataState.SAVE_REQ}
                onClick={(_evt) =>
                  this.props.onConfirmDelete(
                    this.props.id!,
                    this.props.intl.formatMessage(messages.confirmDeleteTitle),
                    this.props.intl.formatMessage(messages.confirmDeleteBody),
                  )
                }
              >
                <FormattedMessage id="Delete Tariff" />
              </Button>
            )}
          </div>
          <div className="button-bar-right">
            <SaveFeedback dataState={this.props.dataState} className="me-2" />
            <Button
              id="cancelTariffSettings"
              variant="secondary"
              className="me-1"
              onClick={this.props.onCancelClick}
            >
              <FormattedMessage id="Cancel" />
            </Button>
            <Button
              id="saveTariffSettings"
              variant="primary"
              onClick={this.props.onSaveClick.bind(this, this.props.id, this.state)}
              disabled={
                this.props.dataState === DataState.SAVE_REQ ||
                !this.state.isChanged ||
                !this.state.name ||
                !this.state.currency
              }
            >
              <FormattedMessage id="Save" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderSettings() {
    return (
      <>
        <Form.Group as={Row} className="mb-3">
          <Form.Label htmlFor="name" column sm={3}>
            <FormattedMessage id="Name" />
          </Form.Label>
          <Col sm={9}>
            {this.props.isRestricted ? (
              <div className="mt-1">{this.props.tariff?.name}</div>
            ) : (
              <Form.Control
                id="name"
                type="text"
                value={this.state.name}
                className={this.state.name ? undefined : "input-error"}
                onChange={(event) => {
                  this.setState({
                    isChanged: true,
                    name: event?.currentTarget.value,
                  });
                }}
              />
            )}
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label htmlFor="currency" column sm={3}>
            <FormattedMessage id="Currency" />
          </Form.Label>
          <Col sm={9}>
            {this.props.isRestricted ? (
              <div className="mt-1">{this.props.tariff?.currency}</div>
            ) : (
              <Form.Control
                id="currency"
                type="text"
                value={this.state.currency}
                className={this.state.currency ? undefined : "input-error"}
                onChange={(event) => {
                  this.setState({
                    isChanged: true,
                    currency: event?.currentTarget.value,
                  });
                }}
              />
            )}
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label htmlFor="priority" column sm={3}>
            <FormattedMessage id="Activated" />
          </Form.Label>
          <Col sm={9} style={{ marginTop: 6 }}>
            <Form.Check
              id="isActive"
              checked={this.state.isActive ?? false}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  isActive: event.currentTarget.checked,
                });
              }}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-3">
          <Form.Label htmlFor="priority" column sm={3}>
            <FormattedMessage id="Priority" />
          </Form.Label>
          <Col sm={9} className="mt-2">
            <InputNumber
              id="priority"
              type="number"
              value={this.state.priority}
              onChange={(value) => {
                if (isSet(value)) {
                  this.setState({
                    isChanged: true,
                    priority: value,
                  });
                }
              }}
            />
            <br />
            <span className="infotext">
              <FormattedMessage id="If multiple tariffs apply, the tariff with highest priority will be used." />
            </span>
          </Col>
        </Form.Group>

        {!this.props.isRestricted && this.renderTariff()}
        {!this.props.isRestricted && this.renderPeriodRestriction()}
        {!this.props.isRestricted && this.renderTimeRestriction()}
      </>
    );
  }

  private renderTariff() {
    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column sm={3}>
          <FormattedMessage id="Costs" />
        </Form.Label>
        <Col sm={9}>
          <Form.Check
            id="chkCostPerEnergy"
            style={{ marginTop: 6 }}
            className="mb-2"
            checked={isSet(this.state.costPerEnergy)}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                costPerEnergy: event.currentTarget.checked ? 0 : undefined,
              });
            }}
            label={this.renderCostPerEnergyLabel()}
          />
          <Form.Check
            id="chkCostPerPeriod"
            checked={isSet(this.state.costPerPeriod)}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                costPerPeriod: event.currentTarget.checked ? 0 : undefined,
              });
            }}
            label={this.renderCostPerPeriodLabel()}
          />
        </Col>
      </Form.Group>
    );
  }

  private renderCostPerEnergyLabel() {
    return isSet(this.state.costPerEnergy) ? (
      <>
        <FormattedMessage id="Cost per energy" />
        <span className="me-1">:</span>
        {isSet(this.state.costPerEnergy) && (
          <>
            <br />
            <InputNumber
              id="costPerEnergy"
              type="number"
              min={0}
              step="0.1"
              value={this.state.costPerEnergy}
              onChange={(number) => {
                if (isSet(number)) {
                  this.setState({ isChanged: true, costPerEnergy: number });
                }
              }}
            />
            <span className="me-4"> {this.state.currency}/kWh</span>
          </>
        )}
      </>
    ) : (
      <FormattedMessage id="Cost per energy" />
    );
  }

  private renderCostPerPeriodLabel() {
    return isSet(this.state.costPerPeriod) ? (
      <>
        <FormattedMessage id="Cost per period" />
        <span className="me-1">:</span>
        {isSet(this.state.costPerPeriod) && (
          <>
            <br />
            <InputNumber
              id="costPerPeriod"
              type="number"
              min={0}
              step="0.1"
              value={this.state.costPerPeriod}
              onChange={(number) => {
                if (isSet(number)) {
                  this.setState({ isChanged: true, costPerPeriod: number });
                }
              }}
            />
            <span className="me-4"> {this.state.currency}/h</span>
          </>
        )}
      </>
    ) : (
      <FormattedMessage id="Cost per period" />
    );
  }

  private renderPeriodRestriction() {
    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column sm={3}>
          <FormattedMessage id="Validity Period" />
        </Form.Label>
        <Col sm={9}>
          <Form.Check
            id="validFrom"
            checked={this.state.validFrom ? true : false}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                validFrom: event.currentTarget.checked ? Math.round(Date.now() / 1000) : undefined,
              });
            }}
            label={this.renderValidFromLabel()}
          />
          <Form.Check
            id="validUntil"
            checked={this.state.validUntil ? true : false}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                validUntil: event.currentTarget.checked ? Math.round(Date.now() / 1000) : undefined,
              });
            }}
            label={this.renderValidUntilLabel()}
          />
        </Col>
      </Form.Group>
    );
  }

  private renderValidFromLabel() {
    return (
      <>
        <FormattedMessage id="Limit this tariff from" />
        {isSet(this.state.validFrom) && this.state.validFrom !== 0 && <>:</>}
        {isSet(this.state.validFrom) &&
          this.state.validFrom !== 0 &&
          (Modernizr.inputtypes["datetime-local"] ? (
            <input
              type="datetime-local"
              name="timestamp"
              style={{ width: 230, marginTop: -5, marginBottom: 10 }}
              className="me-1"
              defaultValue={getLocalDateTimeIso(this.state.validFrom * 1000)}
              onChange={(event) => {
                const newTimestampMs = parseDateTimeEvent(event, this.state.validFrom);
                // gets undefined using "clear", but also by entering a date with zero
                if (newTimestampMs) {
                  this.setState({
                    isChanged: true,
                    validFrom: Math.round(newTimestampMs / 1000),
                  });
                }
              }}
            />
          ) : (
            <>
              <br />
              <input
                type="date"
                name="date"
                style={{ width: 130, marginTop: -5, marginBottom: 10 }}
                value={getLocalDate(this.state.validFrom * 1000)}
                onChange={(event) => {
                  const newTimestampMs = parseDateTimeEvent(event, this.state.validFrom);
                  // gets undefined using "clear", but also by entering a date with zero
                  if (newTimestampMs) {
                    this.setState({
                      isChanged: true,
                      validFrom: Math.round(newTimestampMs / 1000),
                    });
                  }
                }}
              />{" "}
              <input
                type="time"
                name="time"
                style={{ width: 145, marginTop: -5, marginBottom: 10 }}
                value={getLocalTime(this.state.validFrom)}
                onChange={(event) => {
                  const newTimestampMs = parseDateTimeEvent(event, this.state.validFrom);
                  this.setState({
                    isChanged: true,
                    validFrom: newTimestampMs ? Math.round(newTimestampMs / 1000) : undefined,
                  });
                }}
              />
            </>
          ))}
      </>
    );
  }

  private renderValidUntilLabel() {
    return (
      <>
        <FormattedMessage id="Limit this tariff until" />
        {isSet(this.state.validUntil) && this.state.validUntil !== 0 && <>:</>}
        {isSet(this.state.validUntil) &&
          this.state.validUntil !== 0 &&
          (Modernizr.inputtypes["datetime-local"] ? (
            <input
              type="datetime-local"
              name="timestamp"
              style={{ width: 230, marginTop: -5, marginBottom: 10 }}
              className="me-1"
              defaultValue={getLocalDateTimeIso(this.state.validUntil * 1000)}
              onChange={(event) => {
                const newTimestampMs = parseDateTimeEvent(event, this.state.validUntil);
                this.setState({
                  isChanged: true,
                  validUntil: newTimestampMs ? Math.round(newTimestampMs / 1000) : undefined,
                });
              }}
            />
          ) : (
            <>
              <br />
              <input
                type="date"
                name="date"
                style={{ width: 130, marginTop: -5, marginBottom: 10 }}
                value={getLocalDate(this.state.validUntil * 1000)}
                onChange={(event) => {
                  const newTimestampMs = parseDateTimeEvent(event, this.state.validUntil);
                  this.setState({
                    isChanged: true,
                    validUntil: newTimestampMs ? Math.round(newTimestampMs / 1000) : undefined,
                  });
                }}
              />{" "}
              <input
                type="time"
                name="time"
                style={{ width: 145, marginTop: -5, marginBottom: 10 }}
                value={getLocalTime(this.state.validUntil)}
                onChange={(event) => {
                  const newTimestampMs = parseDateTimeEvent(event, this.state.validUntil);
                  this.setState({
                    isChanged: true,
                    validUntil: newTimestampMs ? Math.round(newTimestampMs / 1000) : undefined,
                  });
                }}
              />
            </>
          ))}
      </>
    );
  }

  private renderTimeRestriction() {
    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column sm={3}>
          <FormattedMessage id="Time Restriction" />
        </Form.Label>
        <Col sm={9}>
          <Form.Check
            id="restrictTime"
            style={{ marginTop: 6 }}
            checked={this.state.timeStart ? true : false}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                timeStart: event.currentTarget.checked ? 22 * 3600 : undefined,
                timeEnd: event.currentTarget.checked ? 6 * 3600 : undefined,
              });
            }}
            label={this.renderTimeRestrictionLabel()}
          />
        </Col>
      </Form.Group>
    );
  }

  private renderTimeRestrictionLabel() {
    return isSet(this.state.timeStart) ? (
      <>
        <FormattedMessage id="The time restriction is:" />
        <br />
        <input
          type="time"
          name="time"
          style={{ width: 145 }}
          value={getLocalTime(this.state.timeStart * 1000)}
          onChange={(event) => {
            const newTimestampMs = parseDateTimeEvent(event, this.state.timeStart);
            this.setState({ isChanged: true, timeStart: (newTimestampMs ?? 0) / 1000 });
          }}
        />
        {" - "}
        <input
          type="time"
          name="time"
          style={{ width: 145 }}
          value={getLocalTime(this.state.timeEnd! * 1000)}
          onChange={(event) => {
            const newTimestampMs = parseDateTimeEvent(event, this.state.timeEnd);
            this.setState({ isChanged: true, timeEnd: (newTimestampMs ?? 0) / 1000 });
          }}
        />
        <br />
        <span className="infotext">
          <FormattedMessage id="The time restriction only applies to the start of the charging session. The whole session will be handled like its start." />
        </span>
      </>
    ) : (
      <FormattedMessage id="Set a time restriction" />
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ModalTariffItemComponent));
